import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { AlertCircle, Zap, X } from "lucide-react";
import { api } from "../lib/api";
import { useI18n } from "../lib/i18n";

const DISMISS_KEY = "hosys_billing_banner_dismissed_until";

/**
 * Slim status banner that shows above the page content when the company's
 * subscription is expiring soon (renewal_warning) OR has expired. Hidden
 * everywhere on /app/pricing and /app/billing/* to avoid distraction.
 */
export default function BillingBanner() {
  const { lang } = useI18n();
  const loc = useLocation();
  const [status, setStatus] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // Don't fetch on the pricing/billing pages themselves
    if (loc.pathname.startsWith("/app/pricing") ||
        loc.pathname.startsWith("/app/billing")) {
      setStatus(null);
      return;
    }
    let alive = true;
    (async () => {
      try {
        const r = await api.get("/billing/status");
        if (!alive) return;
        setStatus(r.data);
      } catch {
        /* ignore */
      }
    })();
    return () => { alive = false; };
  }, [loc.pathname]);

  useEffect(() => {
    const until = parseInt(localStorage.getItem(DISMISS_KEY) || "0", 10);
    if (until && Date.now() < until) setDismissed(true);
  }, []);

  if (!status || dismissed) return null;
  if (status.status === "none") return null;          // never subscribed → no banner
  if (!status.renewal_warning && status.status !== "expired") return null;

  const expired = status.status === "expired";
  const isTrial = status.is_trial && !expired;
  const days = Math.max(0, Math.floor(status.days_remaining ?? 0));

  const dismiss = () => {
    // Snooze for 24h
    localStorage.setItem(DISMISS_KEY, String(Date.now() + 86400000));
    setDismissed(true);
  };

  const palette = expired
    ? "bg-rose-500/10 border-rose-500/40 text-rose-100"
    : isTrial
    ? "bg-purple-500/10 border-purple-500/40 text-purple-100"
    : "bg-amber-500/10 border-amber-500/40 text-amber-100";
  const Icon = expired ? AlertCircle : Zap;

  return (
    <div className={`mb-4 rounded-xl border ${palette} px-4 py-3 flex items-center gap-3 text-sm`}
         data-testid="billing-banner">
      <Icon size={16} className="flex-shrink-0" />
      <div className="flex-1">
        <strong className="font-semibold">
          {expired
            ? (lang === "es" ? "Tu suscripción ha expirado" : "La teva subscripció ha expirat")
            : isTrial
            ? (lang === "es"
                ? `Tu prueba gratis acaba en ${days} día${days === 1 ? "" : "s"}`
                : `La prova gratis acaba en ${days} dia${days === 1 ? "" : "es"}`)
            : (lang === "es"
                ? `Renueva en ${days} día${days === 1 ? "" : "s"}`
                : `Renova en ${days} dia${days === 1 ? "" : "es"}`)}
        </strong>
        <span className="opacity-80 ml-2">
          {expired
            ? (lang === "es"
                ? "Reactiva tu plan para seguir usando todas las funciones."
                : "Reactiva el teu pla per continuar usant totes les funcions.")
            : isTrial
            ? (lang === "es"
                ? "Suscríbete ahora para no perder el acceso."
                : "Subscriu-te ara per no perdre l'accés.")
            : (lang === "es"
                ? "Renueva ahora para evitar interrupciones."
                : "Renova ara per evitar interrupcions.")}
        </span>
      </div>
      <Link
        to="/app/pricing"
        className={`px-3 py-1.5 rounded-lg text-xs font-semibold border ${
          expired ? "border-rose-400 hover:bg-rose-500/20"
                  : isTrial ? "border-purple-400 hover:bg-purple-500/20"
                            : "border-amber-400 hover:bg-amber-500/20"
        }`}
        data-testid="billing-banner-cta"
      >
        {expired
          ? (lang === "es" ? "Reactivar" : "Reactiva")
          : isTrial
          ? (lang === "es" ? "Suscribirse" : "Subscriu-te")
          : (lang === "es" ? "Renovar" : "Renovar")}
      </Link>
      {!expired && (
        <button
          type="button"
          onClick={dismiss}
          className="opacity-60 hover:opacity-100"
          aria-label="Dismiss"
          data-testid="billing-banner-dismiss"
        >
          <X size={14} />
        </button>
      )}
    </div>
  );
}
